/**
 * ToastNotification — bridges the notification SSE queue (high/critical
 * priority server-pushed notifications) into the upstream `@archieai/ui`
 * Toast portal.
 *
 * Implements AC29: toast display for high/critical priority notifications.
 * The upstream `showToast` helper (Sonner-backed) handles auto-dismiss,
 * stacking, accessibility (`role="alert"`, polite/assertive aria-live),
 * brand-aware tokens, and dismiss UX. This component is now just an effect
 * that drains the `useNotifications().toasts` queue.
 *
 * Phase 1C cutover: removed local `bg-red-50` / `bg-blue-50` styling and
 * the lucide-react icons — upstream Toast is brand-aware and ships its own
 * icon set.
 */

import { useEffect, useRef } from 'react';
import { showToast } from '@archieai/ui';
import { useNotifications } from './NotificationContext.js';

/**
 * ToastContainer — drains the SSE-driven toast queue into upstream toasts.
 *
 * Renders nothing visible; the upstream `<ToastContainer />` (mounted once in
 * App.tsx) handles all rendering. We just observe newly-arrived notifications
 * and call `showToast.{error,info}` for them.
 */
export function ToastContainer() {
  const { toasts, dismissToast } = useNotifications();
  const firedIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    for (const notification of toasts) {
      if (firedIdsRef.current.has(notification.id)) continue;
      firedIdsRef.current.add(notification.id);

      const isCritical = notification.priority === 'critical';
      const variant = isCritical ? 'error' : 'info';
      const description = notification.body;

      const action = notification.action_url
        ? {
            label: notification.action_label || 'Open',
            onClick: () => {
              window.open(notification.action_url!, '_blank', 'noopener,noreferrer');
              dismissToast(notification.id);
            },
          }
        : undefined;

      showToast[variant](notification.title, description, action);
    }

    // Drop ids no longer in the queue so re-fires are possible if the same
    // id reappears later.
    if (firedIdsRef.current.size > toasts.length) {
      const live = new Set(toasts.map((t) => t.id));
      for (const id of firedIdsRef.current) {
        if (!live.has(id)) firedIdsRef.current.delete(id);
      }
    }
  }, [toasts, dismissToast]);

  return null;
}
